import React, { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { Button, createUseStyles, Icon, Section, Text, Tooltip } from '@Sage/hoi-poi-ui';

import FilterViews from 'components/FilterViews';
import useDnD from 'hooks/useDnD';
import { useWeb5Context } from 'lib/Web5Context';
import { getCrossID } from 'utils/filters';
import { isEqual } from 'utils/objects';

import ModalOptions from '../ModalOptions';
import { FiltersContext } from './Context';
import Empty from './Empty';
import FiltersSection from './FiltersSection';
import useFilters from './hooks/useFilters';
import { fieldOperators, operators } from './operators';
import { defaultValues, inputFields, rangeFields } from './constants';
import styles from './styles';

const {
    EMPTY,
    GREATER,
    GREATER_OR_EQUAL,
    INSIDE_RANGE,
    LESS,
    LESS_OR_EQUAL,
    NOT_EMPTY,
    NO_DATA_WITHIN_RANGE,
    OUTSIDE_RANGE,
} = operators;

const useStyles = createUseStyles(styles, 'FiltersContent');

const Content = memo(
    ({
        avoidQuickFilters,
        canManagePrivateViews,
        canManagePublicViews,
        className,
        crossOperators,
        crossSchema,
        customView,
        entity,
        extraMultiSchema,
        extraSchema,
        getAllowedOperators = () => {},
        getFieldsWithoutAdvancedFilters = () => {},
        getHasAdvancedFilters = () => {},
        isPinnable = true,
        isOpenViewsModal,
        labels,
        labelsDeleteViewsModal,
        labelsSaveViewsModal,
        labelsViews,
        modalOptionsProps,
        onChange,
        onChangeOperator,
        onChangeSelectedFilters,
        onChangeView,
        onChangeCustomView,
        onDelete,
        onDeleteCustomView,
        onDeleteView,
        onDrag,
        onDrop,
        onReOrder,
        onSaveView,
        onToggle,
        onToggleAddModal,
        onToggleSaveViewsModal,
        operators,
        quickSchema = [],
        schema,
        selectedFilters,
        selectedView,
        showAdd,
        toggled = {},
        toggleQuickFilter,
        useGeneralSections = true,
        useHeader,
        useSections = true,
        useSectionsTitle = false,
        useViewsInFilters,
        useViewsInMenu,
        values,
        views,
    }) => {
        // ========================================
        // CONTEXT & CUSTOM HOOKS
        // ========================================
        const classes = useStyles();
        const { t } = useWeb5Context();
        const { setWithActiveAdvancedFilters } = useContext(FiltersContext);

        const {
            getActiveFields,
            handleOnToggleFilter,
            onToggleQuickFilter,
            renderFields,
            renderFilters,
            renderSubfilters,
        } = useFilters({
            classes,
            crossOperators,
            entity,
            getAllowedOperators,
            getFieldsWithoutAdvancedFilters,
            getHasAdvancedFilters,
            isPinnable,
            onToggle,
            operators,
            schema,
            toggleQuickFilter,
            toggled,
            useSections,
            values,
        });

        // ========================================
        // LOCAL STATE
        // ========================================
        const [innerValues, setInnerValues] = useState(values || {});
        const [mappedExtraSchema, setMappedExtraSchema] = useState(null);
        const [numberExtraGroupOptions, setNumberExtraGroupOptions] = useState(0);

        // ========================================
        // REFS
        // ========================================
        const activeEditing = useRef({});
        const oldInnerValues = useRef(null);

        // ========================================
        // DERIVED STATE & MEMOIZATION
        // ========================================
        const classNames = classnames(classes.content, className);
        const hasCrossFilters = useMemo(() => extraMultiSchema?.length > 0, [extraMultiSchema]);

        const isEmpty = useMemo(() => {
            const hasQuickFilters = quickSchema?.length > 0;
            const hasGeneralFilters = schema?.length > 0;
            const hasCross = crossSchema && Object.entries(crossSchema).length > 0;

            return !hasQuickFilters && !hasGeneralFilters && !hasCross;
        }, [crossSchema, quickSchema, schema]);

        // ========================================
        // UTILITY FUNCTIONS
        // ========================================
        const getMappedSchema = useCallback((schemaToMap) => {
            if (!schemaToMap) return { mapping: {}, optionsCount: 0 };

            let optionsCount = 0;
            const mapping = schemaToMap.reduce((obj, current) => {
                const parentId = current.id;

                if (current?.options) {
                    optionsCount += 1;
                    const children = current.options.reduce((obj2, child) => {
                        obj2[child.id] = parentId;
                        return obj2;
                    }, {});
                    return { ...obj, ...children };
                }

                return obj;
            }, {});

            return { mapping, optionsCount };
        }, []);

        // ========================================
        // EVENT HANDLERS & CALLBACKS
        // ========================================
        const commonOnChange = useCallback(
            ({ field, value, info, isGroupChange, crossEntity }) => {
                let newValue = value;

                const finalEntity = crossEntity && !field.isFakeCrossFilter ? crossEntity : null;
                const crossId = getCrossID(field.id, finalEntity);
                const advancedFilters = getHasAdvancedFilters(finalEntity);

                if (advancedFilters && field.dataType === 'probability') {
                    const currentOperators = finalEntity
                        ? (crossOperators?.[finalEntity] ?? {})
                        : operators;
                    const operatorValue = currentOperators[crossId]?.value;
                    const oldValue = innerValues[crossId];
                    let newMinValue = field.inputProps.min;
                    let newMaxValue = field.inputProps.max;

                    if ([LESS, LESS_OR_EQUAL].includes(operatorValue)) {
                        newMinValue = Array.isArray(newValue) ? newValue[0] : newValue;
                        newMaxValue = oldValue?.[1] ?? newMaxValue;
                        newValue = [newMinValue, newMaxValue];
                    }

                    if ([GREATER, GREATER_OR_EQUAL].includes(operatorValue)) {
                        newMinValue = oldValue?.[0] ?? newMinValue;
                        newMaxValue = Array.isArray(newValue) ? newValue[1] : newValue;
                        newValue = [newMinValue, newMaxValue];
                    }
                }

                oldInnerValues.current = { ...innerValues, [crossId]: newValue };
                setInnerValues({ ...innerValues, [crossId]: newValue });

                if (isGroupChange) {
                    const hasValues = value.filter((current) => current).length > 0;
                    if (hasValues && info?.action === 'clear') {
                        return;
                    }
                }

                if (
                    inputFields.includes(field.dataType) &&
                    newValue &&
                    !info?.action &&
                    info?.action !== 'clear'
                ) {
                    return;
                }

                onChange({ filter: field, value: newValue, info, crossEntity: finalEntity });
            },
            [crossOperators, getHasAdvancedFilters, innerValues, onChange, operators],
        );

        const handleOnChangeGroup = useCallback(
            (field, crossEntity) => {
                const finalCrossEntity =
                    crossEntity && !field.isFakeCrossFilter ? crossEntity : null;

                return (groupValue, value, index, event, info) => {
                    commonOnChange({
                        crossEntity: finalCrossEntity,
                        field,
                        info,
                        isGroupChange: true,
                        value: groupValue,
                    });
                };
            },
            [commonOnChange],
        );

        const handleOnChange = useCallback(
            ({ field, crossEntity }) => {
                const finalCrossEntity =
                    crossEntity && !field.isFakeCrossFilter ? crossEntity : null;

                return (value, e, info) => {
                    const crossId = getCrossID(field.id, finalCrossEntity);

                    if (inputFields.includes(field.dataType)) {
                        activeEditing.current[crossId] = true;
                    }

                    const finalInfo =
                        field.dataType === 'singleValueList' ||
                        field.dataType === 'multipleValueList' ||
                        field.dataType === 'relatedValueList'
                            ? e
                            : info;

                    commonOnChange({
                        crossEntity: finalCrossEntity,
                        field,
                        info: finalInfo,
                        value,
                    });
                };
            },
            [commonOnChange],
        );

        const handleOnBlur = useCallback(
            (field, crossEntity) => {
                return (value) => {
                    const finalEntity =
                        crossEntity && !field.isFakeCrossFilter ? crossEntity : null;
                    const crossId = getCrossID(field.id, finalEntity);
                    const advancedFilters = getHasAdvancedFilters(finalEntity);

                    if (activeEditing.current[crossId]) {
                        delete activeEditing.current[crossId];
                    }

                    let operatorValue;
                    if (advancedFilters) {
                        const currentOperators = finalEntity
                            ? (crossOperators?.[finalEntity] ?? {})
                            : operators;
                        operatorValue = currentOperators[field.id]?.value;
                    }

                    if (rangeFields[field.dataType]) {
                        const innerValue = innerValues[crossId]?.filter(Boolean).length ?? 0;

                        if (
                            innerValue === 0 ||
                            innerValue === 2 ||
                            (operatorValue &&
                                innerValue > 0 &&
                                [LESS, LESS_OR_EQUAL, GREATER, GREATER_OR_EQUAL].includes(
                                    operatorValue,
                                ))
                        ) {
                            return onChange({
                                crossEntity,
                                filter: field,
                                value: innerValues[crossId] ?? null,
                            });
                        }
                        return;
                    }

                    return onChange({ crossEntity, filter: field, value });
                };
            },
            [crossOperators, getHasAdvancedFilters, innerValues, onChange, operators],
        );

        const handleChangeOperator = useCallback(
            ({ field, defaultValue, crossEntity }) =>
                (value) => {
                    const newValue = value ?? defaultValue;
                    onChangeOperator?.({ crossEntity, filter: field, value: newValue });

                    if ([EMPTY, NOT_EMPTY].includes(newValue.value)) {
                        handleOnChange({ crossEntity, field })(defaultValues[field.dataType]);
                    } else if (
                        field.dataType === 'date' &&
                        ![INSIDE_RANGE, OUTSIDE_RANGE, NO_DATA_WITHIN_RANGE].includes(
                            newValue.value,
                        )
                    ) {
                        handleOnChange({ crossEntity, field })(defaultValues[field.dataType]);
                    }
                },
            [handleOnChange, onChangeOperator],
        );

        const removeFromSelected = useCallback(
            (filter) => {
                const { id, entity: entityOverride, isFakeCrossFilter } = filter;
                const newSelected = { ...selectedFilters };
                const finalEntity =
                    entityOverride && !isFakeCrossFilter ? entityOverride : entity.entity;

                const getGroupOptions = () => {
                    if (hasCrossFilters) {
                        return (
                            newSelected[finalEntity]?.options[mappedExtraSchema[finalEntity][id]]
                                ?.options ?? []
                        );
                    }
                    return newSelected[mappedExtraSchema[id]]?.options ?? [];
                };

                const groupOptions = getGroupOptions();
                const indexOption = groupOptions.indexOf(id);

                if (indexOption !== -1) {
                    groupOptions.splice(indexOption, 1);

                    if (hasCrossFilters) {
                        newSelected[finalEntity].options[
                            mappedExtraSchema[finalEntity][id]
                        ].options = groupOptions;
                    } else {
                        newSelected[mappedExtraSchema[id]].options = groupOptions;
                    }

                    onChangeSelectedFilters(newSelected);
                }
            },
            [entity, hasCrossFilters, mappedExtraSchema, onChangeSelectedFilters, selectedFilters],
        );

        const handleOnDelete = useCallback(
            (filter) => {
                return () => {
                    const newInnerValues = { ...innerValues };
                    const { crossId, id, isFakeCrossFilter } = filter;
                    const ID = crossId && !isFakeCrossFilter ? crossId : id;

                    if (activeEditing.current[ID]) {
                        delete activeEditing.current[ID];
                    }

                    delete newInnerValues[ID];
                    setInnerValues(newInnerValues);
                    removeFromSelected(filter);

                    onDelete?.(crossId ?? id);
                };
            },
            [innerValues, onDelete, removeFromSelected],
        );

        const handleDragStart = useCallback(
            (result) => {
                onDrag?.(result);
            },
            [onDrag],
        );

        const handleDragEnd = useCallback(
            (result) => {
                onDrop?.(result);
            },
            [onDrop],
        );

        const handleReorder = useCallback(
            (fromIndex, toIndex) => {
                onReOrder?.(fromIndex, toIndex);
            },
            [onReOrder],
        );

        const getQuickFilterPin = useCallback(
            (filter) => {
                if (Object.hasOwn(filter, 'isPinnable') && !filter.isPinnable) return null;

                const ID = filter.crossId ?? filter.id;
                return (
                    <Tooltip content={t('label_filters_unpin_desc')} placement="top">
                        <span>
                            <Icon
                                className={classes.iconSection}
                                name="unpin"
                                onClick={onToggleQuickFilter(ID)}
                            />
                        </span>
                    </Tooltip>
                );
            },
            [classes.iconSection, onToggleQuickFilter, t],
        );

        // ========================================
        // DND HOOK
        // ========================================
        const {
            containerProps: dndContainerProps,
            getDragHandleProps,
            getItemProps,
            isDragging,
        } = useDnD({
            isDisabled: quickSchema?.length <= 1,
            items: quickSchema,
            onDragEnd: handleDragEnd,
            onDragStart: handleDragStart,
            onReorder: handleReorder,
        });

        // ========================================
        // MEMOIZED PROPS
        // ========================================
        const renderFieldsProps = useMemo(
            () => ({
                handleChangeOperator,
                handleOnBlur,
                handleOnChange,
                handleOnChangeGroup,
                innerValues,
            }),
            [handleChangeOperator, handleOnBlur, handleOnChange, handleOnChangeGroup, innerValues],
        );

        const renderFiltersProps = useMemo(
            () => ({
                handleChangeOperator,
                handleOnBlur,
                handleOnChange,
                handleOnChangeGroup,
                handleOnDelete,
                innerValues,
            }),
            [
                handleChangeOperator,
                handleOnBlur,
                handleOnChange,
                handleOnChangeGroup,
                handleOnDelete,
                innerValues,
            ],
        );

        // ========================================
        // EFFECTS
        // ========================================
        useEffect(() => {
            if (!isEqual(oldInnerValues?.current, values)) {
                setInnerValues((currentInnerValues) => {
                    const updatedValues = { ...values };

                    Object.keys(activeEditing.current).forEach((fieldId) => {
                        if (
                            activeEditing.current[fieldId] &&
                            currentInnerValues[fieldId] !== undefined
                        ) {
                            updatedValues[fieldId] = currentInnerValues[fieldId];
                        }
                    });

                    return updatedValues;
                });
            }
        }, [values]);

        useEffect(() => {
            if (!toggled) return;

            Object.keys(toggled).forEach((sectionId) => {
                const section = toggled[sectionId];
                const isOpen = typeof section === 'boolean' ? section : section?.open;

                if (!isOpen) {
                    Object.keys(activeEditing.current).forEach((fieldId) => {
                        if (fieldId.startsWith(sectionId) || fieldId === sectionId) {
                            delete activeEditing.current[fieldId];
                        }
                    });
                }
            });
        }, [toggled]);

        useEffect(() => {
            if (!hasCrossFilters && !mappedExtraSchema && extraSchema?.length > 0) {
                const { mapping, optionsCount } = getMappedSchema(extraSchema);
                setMappedExtraSchema(mapping);
                setNumberExtraGroupOptions(optionsCount);
                return;
            }

            if (hasCrossFilters && !mappedExtraSchema) {
                let totalOptionsCount = 0;
                const newMappedExtraSchema = extraMultiSchema.reduce((acc, { id, schema: s }) => {
                    const { mapping, optionsCount } = getMappedSchema(s);
                    totalOptionsCount += optionsCount;
                    acc[id] = mapping;
                    return acc;
                }, {});
                setMappedExtraSchema(newMappedExtraSchema);
                setNumberExtraGroupOptions(totalOptionsCount);
            }
        }, [extraMultiSchema, extraSchema, getMappedSchema, hasCrossFilters, mappedExtraSchema]);

        useEffect(() => {
            if (!operators && !crossOperators) return;

            let hasActiveAdvancedFilters = false;
            const filters = [...quickSchema, ...schema].filter(Boolean);

            const checkOperators = (ops) => {
                for (const [key, value] of Object.entries(ops)) {
                    const operatorValue = value.value;
                    const filter = filters.find((item) => item.id === key);
                    if (!filter) continue;

                    const dataType = filter.fields[0]?.dataType;
                    const defaultValue = dataType && fieldOperators[dataType]?.default;

                    if (operatorValue !== defaultValue) {
                        return true;
                    }
                }
                return false;
            };

            if (checkOperators(operators)) {
                hasActiveAdvancedFilters = true;
            }

            for (const key in crossOperators) {
                if (checkOperators(crossOperators[key])) {
                    hasActiveAdvancedFilters = true;
                }
            }

            setWithActiveAdvancedFilters(hasActiveAdvancedFilters);
        }, [crossOperators, operators, quickSchema, schema, setWithActiveAdvancedFilters]);

        // ========================================
        // RENDER: QUICK FILTERS
        // ========================================
        const renderQuickFilters = useMemo(() => {
            if (!quickSchema?.length) return null;

            return (
                <FiltersSection
                    filterNum={quickSchema.length}
                    title={t('label_filters_section_quickfilters')}
                >
                    <div
                        {...dndContainerProps}
                        className={classnames({ [classes.filterDragging]: isDragging })}
                    >
                        {quickSchema.map((filter, index) => {
                            const ID = filter.crossId ?? filter.id;
                            const sectionClasses = classnames(
                                classes.filter,
                                `section-filter__field__${ID}`,
                                { [classes.crossFilter]: !!filter.crossId },
                            );

                            const title =
                                filter.label && filter.subLabel ? (
                                    <div className={classes.labelMultiline}>
                                        <Text medium type="subtitle">
                                            {filter.label}
                                        </Text>
                                        <Text type="caption">{filter.subLabel}</Text>
                                    </div>
                                ) : (
                                    filter.label
                                );

                            const hasSubfilters = !!filter.subfilterSchema;
                            const toggledFilter = toggled[ID];
                            const isOpen =
                                typeof toggledFilter === 'boolean'
                                    ? toggledFilter
                                    : (toggledFilter?.open ?? false);

                            const itemProps = getItemProps(index);
                            const dragHandleProps = getDragHandleProps(index);

                            return (
                                <div key={ID} {...itemProps}>
                                    <Section
                                        activeFields={getActiveFields(filter)}
                                        className={sectionClasses}
                                        classes={{
                                            header: classes.filterHeader,
                                            open: isOpen ? classes.filterHeaderOpen : null,
                                        }}
                                        headerPostComponent={getQuickFilterPin(filter)}
                                        headerPreComponent={
                                            quickSchema.length > 1 && (
                                                <span {...dragHandleProps}>
                                                    <Icon
                                                        className={classnames(
                                                            classes.iconSection,
                                                            classes.iconDrag,
                                                        )}
                                                        name="dragAndDrop"
                                                    />
                                                </span>
                                            )
                                        }
                                        isOpen={isOpen}
                                        onChange={
                                            isDragging
                                                ? undefined
                                                : handleOnToggleFilter({
                                                      filter: ID,
                                                      hasSubfilters,
                                                  })
                                        }
                                        onRemove={
                                            filter.isRemovable ? handleOnDelete(filter) : undefined
                                        }
                                        title={title}
                                    >
                                        {renderFields({
                                            ...renderFieldsProps,
                                            crossEntity: filter.entity,
                                            fields: filter.fields,
                                        })}
                                        {renderSubfilters({
                                            ...renderFiltersProps,
                                            className: classes.subFilters,
                                            parentFilter: ID,
                                            renderer: renderFilters,
                                            schema: filter.subfilterSchema,
                                            toggled: toggled[ID]?.subfilters ?? {},
                                        })}
                                    </Section>
                                </div>
                            );
                        })}
                    </div>
                </FiltersSection>
            );
        }, [
            classes.crossFilter,
            classes.filter,
            classes.filterHeader,
            classes.filterHeaderOpen,
            classes.iconDrag,
            classes.iconSection,
            classes.filterDragging,
            classes.labelMultiline,
            classes.subFilters,
            dndContainerProps,
            getActiveFields,
            getDragHandleProps,
            getItemProps,
            getQuickFilterPin,
            handleOnDelete,
            handleOnToggleFilter,
            isDragging,
            quickSchema,
            renderFields,
            renderFieldsProps,
            renderFilters,
            renderFiltersProps,
            renderSubfilters,
            t,
            toggled,
        ]);

        // ========================================
        // RENDER: CROSS FILTERS
        // ========================================
        const renderCrossFilters = useMemo(() => {
            if (!crossSchema || !Object.entries(crossSchema).length) return null;

            return Object.entries(crossSchema).map(([key, schemaItem]) => (
                <React.Fragment key={key}>
                    {renderFilters({ ...renderFiltersProps, ...schemaItem })}
                </React.Fragment>
            ));
        }, [crossSchema, renderFilters, renderFiltersProps]);

        // ========================================
        // RENDER: FILTERS SECTIONS
        // ========================================
        const renderFiltersSections = useMemo(() => {
            const showGeneralFilters = quickSchema?.length > 0 || schema?.length > 0;

            const overrideTitle = {
                activities: t('label_filters_cross_filters_section_activity'),
                companies: t('label_filters_cross_filters_section_account'),
                contacts: t('label_filters_cross_filters_section_contact'),
                opportunities: t('label_filters_cross_filters_section_opportunity'),
                salesOrders: t('label_filters_cross_filters_section_salesorders'),
            }[entity?.entity];

            if (crossSchema && Object.entries(crossSchema).length) {
                return (
                    <>
                        {useGeneralSections ? (
                            <>
                                {showGeneralFilters && (
                                    <FiltersSection title={t('label_general_filters')}>
                                        {!avoidQuickFilters && renderQuickFilters}
                                        {renderFilters({
                                            ...renderFiltersProps,
                                            title: overrideTitle,
                                        })}
                                    </FiltersSection>
                                )}
                                <FiltersSection title={t('label_related_entities')}>
                                    {renderCrossFilters}
                                </FiltersSection>
                            </>
                        ) : (
                            <>
                                {!avoidQuickFilters && renderQuickFilters}
                                {renderFilters({ ...renderFiltersProps, title: overrideTitle })}
                                {renderCrossFilters}
                            </>
                        )}
                    </>
                );
            }

            return (
                <>
                    {!avoidQuickFilters && renderQuickFilters}
                    {useSectionsTitle
                        ? renderFilters({ ...renderFiltersProps, title: overrideTitle })
                        : renderFilters(renderFiltersProps)}
                </>
            );
        }, [
            avoidQuickFilters,
            crossSchema,
            entity?.entity,
            quickSchema?.length,
            renderCrossFilters,
            renderFilters,
            renderFiltersProps,
            renderQuickFilters,
            schema?.length,
            t,
            useGeneralSections,
            useSectionsTitle,
        ]);

        // ========================================
        // RENDER
        // ========================================
        return (
            <div className={classNames}>
                <div
                    className={classnames(classes.filtersWrapper, {
                        [classes.withAddButton]: showAdd && !useHeader,
                        [classes.withHeader]: useHeader && !showAdd,
                        [classes.withHeaderAndAddButton]: useHeader && showAdd,
                    })}
                >
                    {(useViewsInFilters || useViewsInMenu) && (
                        <FilterViews
                            canManagePrivateViews={canManagePrivateViews}
                            canManagePublicViews={canManagePublicViews}
                            customView={customView}
                            handleOnToggleFilter={handleOnToggleFilter}
                            isOpen={isOpenViewsModal}
                            labels={labelsViews}
                            labelsDelete={labelsDeleteViewsModal}
                            labelsSave={labelsSaveViewsModal}
                            onChange={onChangeView}
                            onChangeCustomView={onChangeCustomView}
                            onDelete={onDeleteView}
                            onDeleteCustomView={onDeleteCustomView}
                            onSave={onSaveView}
                            onToggleSaveViewsModal={onToggleSaveViewsModal}
                            selectedView={selectedView}
                            toggled={toggled}
                            useViewsInFilters={useViewsInFilters}
                            useViewsInMenu={useViewsInMenu}
                            views={views}
                        />
                    )}
                    {renderFiltersSections}
                    {isEmpty && <Empty />}
                </div>
                {showAdd && (
                    <div className={classes.addButton}>
                        <Button
                            icon="plus"
                            isDisabled={numberExtraGroupOptions === 0}
                            isFullWidth
                            onClick={onToggleAddModal}
                            type="secondary"
                        >
                            {labels?.addFields ?? t('label_add_filters')}
                        </Button>
                    </div>
                )}
                <ModalOptions {...modalOptionsProps} />
            </div>
        );
    },
);

Content.displayName = 'Content';

Content.propTypes = {
    labels: PropTypes.shape({
        addFields: PropTypes.string,
        deleteField: PropTypes.string,
    }),
    labelsDeleteViewsModal: PropTypes.shape({
        adviceText: PropTypes.string,
        cancelText: PropTypes.string,
        confirmText: PropTypes.string,
        confirmWord: PropTypes.string,
        confirmWritePlaceholder: PropTypes.string,
        confirmWriteText: PropTypes.string,
        questionText: PropTypes.string,
        title: PropTypes.string,
    }),
    labelsSaveViewsModal: PropTypes.shape({
        cancelText: PropTypes.string,
        confirmText: PropTypes.string,
        saveAs: PropTypes.string,
        saveAsPlaceholder: PropTypes.string,
        title: PropTypes.string,
        viewName: PropTypes.string,
        viewNamePlaceholder: PropTypes.string,
    }),
    labelsViews: PropTypes.shape({
        views: PropTypes.string,
        viewsLoadingPlaceholder: PropTypes.string,
        viewsNoOptionsPlaceholder: PropTypes.string,
        viewsPlaceholder: PropTypes.string,
    }),
    onChangeOperator: PropTypes.func,
    onToggle: PropTypes.func,
    operators: PropTypes.object,
    schema: PropTypes.arrayOf(
        PropTypes.shape({
            fields: PropTypes.arrayOf(
                PropTypes.shape({
                    dataType: PropTypes.string,
                    id: PropTypes.string,
                    inputAttrs: PropTypes.shape({
                        placeholder: PropTypes.string,
                    }),
                    label: PropTypes.string,
                    labelMode: PropTypes.oneOf(['horizontal', 'vertical']),
                }),
            ),
            filterTooltip: PropTypes.oneOfType([
                PropTypes.string,
                PropTypes.shape({
                    description: PropTypes.string,
                    icon: PropTypes.string,
                    title: PropTypes.string,
                }),
            ]),
            id: PropTypes.string,
            isRemovable: PropTypes.bool,
            label: PropTypes.string,
        }),
    ),
    showAdd: PropTypes.bool,
    toggled: PropTypes.object,
    values: PropTypes.object,
};

export default Content;
